import React, { useState, useEffect, useRef, useCallback } from 'react';

const TimeSelectionModal = ({ isOpen, onClose, onSelectTime }) => {
  const [selectedTime, setSelectedTime] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [customTime, setCustomTime] = useState('');
  const [customTimeError, setCustomTimeError] = useState('');
  const intervalRef = useRef(null);
  const customTimeRef = useRef(customTime);
  const prevIsOpenRef = useRef(isOpen);

  customTimeRef.current = customTime;

  const generateTimeSlots = useCallback((selectFirstIfNoCustom = false) => {
    const slots = [];
    const now = new Date();
    
    // Генерируем слоты: 15, 30, 45, 60 минут от текущего времени
    // Слоты обновляются каждую минуту, поэтому всегда актуальные
    for (let i = 0; i < 4; i++) {
      const slotTime = new Date(now.getTime() + (15 + i * 15) * 60000);
      const minutesFromNow = 15 + i * 15;
      
      slots.push({
        time: slotTime,
        label: slotTime.toLocaleTimeString('ru-RU', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        minutesFromNow: minutesFromNow
      });
    }
    
    setTimeSlots(slots);
    
    // Выбираем первый слот только при открытии модалки или при обновлении слотов без своего времени
    if (selectFirstIfNoCustom && !customTimeRef.current && slots.length > 0) {
      setSelectedTime(slots[0]);
    }
  }, []);

  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen) {
      if (justOpened) {
        setCustomTime('');
        setSelectedTime(null);
        setCustomTimeError('');
        generateTimeSlots(true);
      }
      
      // Обновляем слоты каждую минуту (без сброса своего времени)
      intervalRef.current = setInterval(() => {
        generateTimeSlots(true);
      }, 60000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isOpen, generateTimeSlots]);

  const validateCustomTime = (timeString) => {
    const now = new Date();
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return 'Неверный формат времени';
    }
    
    // Создаем время на сегодня
    const customDate = new Date();
    customDate.setHours(hours);
    customDate.setMinutes(minutes);
    customDate.setSeconds(0);
    customDate.setMilliseconds(0);
    
    // Минимальное время - через 15 минут от текущего времени
    const minTime = new Date(now.getTime() + 15 * 60000);
    
    // Проверяем, что выбранное время сегодня и не раньше минимального
    if (customDate < minTime) {
      const diffMinutes = Math.round((minTime - now) / 60000);
      return `Минимальное время сегодня: ${minTime.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })} (через ${diffMinutes} мин)`;
    }
    
    return null;
  };

  const handleCustomTimeChange = (e) => {
    const value = e.target.value;
    setCustomTime(value);
    
    if (value) {
      const error = validateCustomTime(value);
      setCustomTimeError(error || '');
      
      if (!error) {
        const [hours, minutes] = value.split(':').map(Number);
        const customDate = new Date();
        customDate.setHours(hours);
        customDate.setMinutes(minutes);
        customDate.setSeconds(0);
        customDate.setMilliseconds(0);
        
        setSelectedTime({
          time: customDate,
          label: value,
          minutesFromNow: Math.round((customDate - new Date()) / 60000)
        });
      } else {
        setSelectedTime(null);
      }
    } else {
      setCustomTimeError('');
      setSelectedTime(null);
    }
  };

  const handleConfirm = () => {
    if (selectedTime?.time) {
      // Передаём объект с временем — и для своего времени, и для готовых слотов один формат
      onSelectTime({
        time: selectedTime.time,
        label: selectedTime.label,
        minutesFromNow: selectedTime.minutesFromNow
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Выберите время получения</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <p className="modal-description">
            Выберите удобное время для получения заказа (минимум через 15 минут)
          </p>
          
          {/* Свое время */}
          <div className="custom-time-input-container">
            <label htmlFor="custom-time" className="custom-time-label">
              ⏰ Свое время:
            </label>
            <input
              id="custom-time"
              type="time"
              className="custom-time-input"
              value={customTime}
              onChange={handleCustomTimeChange}
              placeholder="ЧЧ:ММ"
            />
            {customTimeError && (
              <div className="custom-time-error">{customTimeError}</div>
            )}
            {selectedTime && customTime && !customTimeError && (
              <div className="custom-time-success">
                ✓ Время получения: {selectedTime.label} (через {selectedTime.minutesFromNow} мин)
              </div>
            )}
          </div>
          
          {/* Разделитель */}
          <div className="time-divider">
            <span>или выберите готовый вариант</span>
          </div>
          
          {/* Готовые варианты */}
          <div className="time-slots-grid">
            {timeSlots.map((slot, index) => (
              <button
                key={index}
                className={`time-slot-btn ${selectedTime?.label === slot.label && !customTime ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedTime(slot);
                  setCustomTime('');
                  setCustomTimeError('');
                }}
              >
                <div className="time-slot-time">{slot.label}</div>
                <div className="time-slot-info">через {slot.minutesFromNow} мин</div>
              </button>
            ))}
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Отмена
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleConfirm}
            disabled={!selectedTime || !!customTimeError}
          >
            Подтвердить
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeSelectionModal;

